// IE Hub - Screen Renderer
// Main component for rendering JSON-driven screens
// render Onboarding and Profile screens

import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import {
  alpha,
  Box, 
  UIButton as Button, 
  Container, 
  Grid, 
  Paper,
  Typography,
  useTheme,
  Link
} from '../../ui';
import { FormHelperText } from '@mui/material';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import TextField from '../../ui/fields/TextField';
import SpecialTextField from '../../ui/fields/SpecialTextField';
import Select from '../../ui/fields/Select';
import Checkbox from '../../ui/fields/Checkbox';
import CheckboxGroup from '../../ui/fields/CheckboxGroup';
import RadioGroup from '../../ui/fields/RadioGroup';
import DateField from '../../ui/fields/DateField';
import ToggleButton from '../../ui/fields/ToggleButton';
import Counter from '../../ui/fields/Counter';
import DateWithClear from '../../ui/fields/DateWithClear';
import CountLabel from '../../ui/fields/CountLabel';
// Visibility evaluation is handled by parseVisibilityCondition helper
import { evaluateNode, evaluateVisibilityRule, VisibilityNode } from '../runtime/visibility/dsl';
import { runEffects } from '../actions/effects';
import InfoDrawer from '../../components/InfoDrawer';
import { infoContentSchema } from '../../shared/types/infoContent';
import SectionHelperText from '../../components/SectionHelperText';
import privacyJson from '../../knowledge/privacy.json';
import { calculateAge } from '@/shared/lib/utils/others';
import { UI_MESSAGES } from '@/constants/ui';

// Import custom components for budget screen
import QuestionGroup from '../../components/QuestionGroup';
import TextWithTooltip from '../../components/TextWithTooltip';
import SelectToggle from '../../components/SelectToggle';
import CollapsibleTextArea from '../../components/CollapsibleTextArea';
import {
  ReferenceHelpConfig,
  resolveReferenceHelp,
} from './referenceHelp';



interface FieldOption {
  value: string | number;
  label: string;
  title?: string;
  description?: string;
}

interface ValidationRule {
  type: string;
  value: string | number;
  message?: string;
  flags?: string;
}

interface ListConfig {
  listFilterQuery?: unknown;
  dynamicSource?: {
    sourceMapping?: unknown[];
  };
}

interface FieldEventActionGroup {
  when?: VisibilityNode;
  actions: unknown[];
}

interface FieldEvents {
  onChange?: FieldEventActionGroup[];
  onMount?: FieldEventActionGroup[];
  onBlur?: FieldEventActionGroup[];
}

interface FieldConfig {
  fieldName: string;
  dataType: string;
  label: string | null;
  isHidden?: boolean;
  isEditable?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  size?: number;
  maxLength?: number;
  helpText?: string;
  requiredErrorMessage?: string;
  validationRules?: ValidationRule[];
  visibilityCondition?: string;
  visibleWhen?: VisibilityNode;
  requiredWhen?: VisibilityNode;
  disabledWhen?: VisibilityNode;
  valueExpression?: VisibilityNode;
  options?: FieldOption[];
  section?: string;
  list?: ListConfig;
  defaultValue?: unknown;
  order?: number;
  events?: FieldEvents;
  // Custom data types for budget screen
  children?: FieldConfig[];
  tooltipVisible?: boolean;
  tooltipContent?: React.ReactNode;
  // Question group specific properties
  showCalculatedIncome?: boolean;
  calculatedIncomeTooltip?: string;
  // Additional properties from JSON schema
  addToPayload?: boolean;
  // When false, field value will NOT be masked in LogRocket recordings. Defaults to true (masked).
  dataPrivate?: boolean;
  ui?: {
    placeholder?: string;
    helperTop?: string;
    helperAttached?: string;
    helperBottom?: string;
    showErrorBelow?: boolean;
    showErrorOnSection?: boolean;
    searchable?: boolean;
    // Optional label for Select dialog header
    dialogLabel?: string;
    // Select: allow user to type their own value if option not present
    allowCustomEntry?: boolean;
    customEntryLinkText?: string;
    customEntryPlaceholder?: string;
    customEntryConfirmText?: string;
    customEntryCancelText?: string;
    // Optional typography variant override for field label
    // Defaults to 'subtitle1' in field components when not provided
    labelVariant?: 'subtitle1' | 'body1' | 'body2';
    // Separator specific UI knobs (for dataType: 'separator')
    separatorVariant?: 'solid' | 'dashed';
    separatorThickness?: number;
    separatorColor?: string; // theme color key like 'divider' or 'primary.main'
    separatorSpacingY?: number; // vertical spacing around the line (theme spacing units)
    // TextField: allow user to indicate they don't have a value via checkbox
    allowNoValue?: boolean;
    noValueCheckboxLabel?: string;
    noValueFieldName?: string;
    // SpecialTextField: help dialog for field instructions
    helpLinkText?: string;
    helpDialog?: {
      title: string;
      instructionsTitle?: string;
      steps: Array<{
        stepNumber?: number;
        text: string;
      }>;
      image?: string;
      imageAlt?: string;
      bottomLinkText?: string;
      onBottomLinkClick?: () => void;
    };
    referenceHelp?: ReferenceHelpConfig;
     // Counter specific UI knobs (for dataType: 'counter')
    min?: number;
    max?: number;
    limitMessage?: string;
  };
}

interface SectionConfig {
  sectionName: string;
  sectionLabel?: string;
  sectionHelperText?: string;
  sectionHelperTextHtml?: boolean;
  sectionHelperTextWordLimit?: number;
  isArray?: boolean;
  fields: FieldConfig[];
  isTable?: boolean;
  // Optional help topic for this section
  helpTopicId?: string;
}

// Actions configuration available at the schema level
type SchemaAction = (
  | { type: 'navigate'; to: string }
  | { type: string; [key: string]: unknown }
);

interface SchemaButtonsConfig {
  primaryText?: string;
  secondaryText?: string;
  // When true, and the schema has zero fields, show secondary button (e.g. Skip)
  showSecondaryWhenNoFields?: boolean;
  // Currently only supported special action is 'skip' (advance without saving)
  secondaryAction?: 'skip';
  // Force show secondary action regardless of field count
  showSecondary?: boolean;
}

interface SchemaActions {
  onSuccess?: SchemaAction[];
  // Optional direct override for the primary button text
  nextButtonText?: string;
  // Optional richer buttons configuration
  buttons?: SchemaButtonsConfig;
}

interface Schema {
  _id?: { $oid: string };
  formName: string;
  collectionName?: string;
  version: number;
  sections: SectionConfig[];
  isActive?: boolean;
  actions?: SchemaActions;
  // Optional help topic for this form
  helpTopicId?: string;
}

interface ScreenProps {
  schema: Schema;
  onSubmit?: (data: unknown) => void; 
  initialData?: Record<string, unknown>;
  hideHeader?: boolean;
  hideActions?: boolean;
  formId?: string;
  // Emits live form values as they change
  onValuesChange?: (data: Record<string, unknown>) => void;
}

interface FormData {
  [key: string]: unknown;
}

function deriveWarningsCategoryKey(params: {
  helpTopicId?: string;
  collectionName?: string;
}): string | undefined {
  const { helpTopicId, collectionName } = params;

  // Budget schemas follow: budget.<group>.<category> (e.g. budget.fixedExpense.school)
  if (typeof helpTopicId === 'string' && helpTopicId.startsWith('budget.')) {
    const parts = helpTopicId.split('.');
    const category = parts.length >= 3 ? parts[2] : undefined;
    if (category && category.length > 0) {
      return category;
    }
  }

  // Fallback for older schemas: Budget_FixedExpense_School -> school
  if (typeof collectionName === 'string' && collectionName.startsWith('Budget_')) {
    const tail = collectionName.split('_').pop();
    if (!tail) return undefined;
    return tail.charAt(0).toLowerCase() + tail.slice(1);
  }

  return undefined;
}

function Screen({ schema, onSubmit, initialData, hideHeader, hideActions, formId = 'onboarding-form', onValuesChange }: ScreenProps) {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const privacyContent = useMemo(() => infoContentSchema.parse(privacyJson), []);
  const warningsCategoryKey = useMemo(
    () =>
      deriveWarningsCategoryKey({
        helpTopicId: schema.helpTopicId,
        collectionName: schema.collectionName,
      }),
    [schema.collectionName, schema.helpTopicId]
  );

  // Helper function to process dynamic UI properties
  const processDynamicValue = useCallback((value: unknown, formData: Record<string, unknown>) => {
    if (typeof value === 'string' && value.includes('{') && value.includes('}')) {
      // Simple template replacement for {fieldName} patterns
      return value.replace(/\{(\w+)\}/g, (match, fieldName) => {
        const fieldValue = formData[fieldName];
        return fieldValue ? String(fieldValue) : match;
      });
    }
    return value;
  }, []);

  // Helper function to parse simple visibility conditions
  const parseVisibilityCondition = useCallback((condition: string, formData: Record<string, unknown>) => {
    if (!condition) return true;
    
    try {
      // Handle simple conditions like "(fieldName) !== ''" or "(fieldName) !== 'value'"
      // This is a simplified parser for basic conditions
      const trimmed = condition.trim();
      
      
      // Match patterns like (fieldName) !== '' && (fieldName2) !== true
      const complexAndMatch = trimmed.match(/^\((\w+)\)\s*!==\s*'([^']*)'\s*&&\s*\((\w+)\)\s*!==\s*(true|false|'[^']*')$/);
      if (complexAndMatch) {
        const [, field1, value1, field2, value2] = complexAndMatch;
        const fieldValue1 = field1 ? formData[field1] : undefined;
        const fieldValue2 = field2 ? formData[field2] : undefined;
        
        // Handle first condition (usually string comparison)
        let condition1: boolean;
        if (value1 === '') {
          condition1 = Boolean(fieldValue1 && fieldValue1 !== '');
        } else {
          condition1 = fieldValue1 !== value1;
        }
        
        // Handle second condition (could be boolean or string)
        let condition2: boolean;
        if (value2 === 'true') {
          condition2 = fieldValue2 !== true;
        } else if (value2 === 'false') {
          condition2 = fieldValue2 !== false;
        } else if (value2 && value2.startsWith("'") && value2.endsWith("'")) {
          // Remove quotes for string comparison
          const stringValue = value2.slice(1, -1);
          condition2 = fieldValue2 !== stringValue;
        } else {
          condition2 = fieldValue2 !== value2;
        }
        
        return condition1 && condition2;
      }
      
      // Match simple patterns like (fieldName) !== ''
      const simpleNeqMatch = trimmed.match(/^\((\w+)\)\s*!==\s*'([^']*)'$/);
      if (simpleNeqMatch) {
        const [, fieldName, value] = simpleNeqMatch;
        const fieldValue = fieldName ? formData[fieldName] : undefined;
        
        if (value === '') {
          return Boolean(fieldValue && fieldValue !== '');
        } else {
          return fieldValue !== value;
        }
      }
      
      // Match patterns like (fieldName) === 'value'
      const eqMatch = trimmed.match(/^\((\w+)\)\s*===\s*'([^']*)'/);
      if (eqMatch) {
        const [, fieldName, expectedValue] = eqMatch;
        return fieldName ? formData[fieldName] === expectedValue : false;
      }
      
      // Match patterns like (fieldName) !== true or (fieldName) === true
      const boolMatch = trimmed.match(/^\((\w+)\)\s*(===|!==)\s*(true|false)$/);
      if (boolMatch) {
        const [, fieldName, operator, boolValue] = boolMatch;
        const fieldValue = fieldName ? formData[fieldName] : undefined;
        const expectedBool = boolValue === 'true';
        
        if (operator === '===') {
          return fieldValue === expectedBool;
        } else {
          return fieldValue !== expectedBool;
        }
      }
      
      // Default to true for unrecognized patterns
      return true;
    } catch (error) {
      console.warn('Error parsing visibility condition:', condition, error);
      return true;
    }
  }, []);

  // Helper to safely evaluate DSL visibility expressions
  const evaluateRule = useCallback((rule: VisibilityNode | undefined, formData: Record<string, unknown>, extraContext: Record<string, unknown> = {}) => {
    if (!rule) return true;
    try {
      return evaluateVisibilityRule(rule, { ...formData, ...extraContext });
    } catch {
      return true;
    }
  }, []);

  const computeIsVisible = useCallback((field: FieldConfig, currentFormValues: Record<string, unknown>) => {
    if (field.isHidden) return false;
    if (field.visibleWhen !== undefined) {
      return evaluateRule(field.visibleWhen, currentFormValues);
    }
    if (field.visibilityCondition) {
      return parseVisibilityCondition(field.visibilityCondition, currentFormValues);
    }
    return true;
  }, [evaluateRule, parseVisibilityCondition]);

  const computeIsDisabled = useCallback((field: FieldConfig, currentFormValues: Record<string, unknown>) => {
    let disabled = Boolean(field.isReadOnly || !field.isEditable);
    if (field.disabledWhen !== undefined) {
      disabled = disabled || evaluateRule(field.disabledWhen, currentFormValues);
    }
    return disabled;
  }, [evaluateRule]);

  const computeShouldBeRequired = useCallback((field: FieldConfig, _hasDynamicBehavior: boolean, currentFormValues: Record<string, unknown>) => {
    // Hidden or disabled fields must never be required
    if (!field.isRequired) return false;
    const visible = computeIsVisible(field, currentFormValues);
    const disabled = computeIsDisabled(field, currentFormValues);
    if (!visible || disabled) return false;

    // When a dynamic condition is provided, enforce it only if the field is visible and enabled
    if (field.requiredWhen !== undefined) {
      return evaluateRule(field.requiredWhen, currentFormValues) === true;
    }

    // Otherwise, required when visible and enabled
    return true;
  }, [evaluateRule, computeIsDisabled, computeIsVisible]);

  // triggerFieldEvents is defined after form initialization

  // Create Zod validation schema from field validations
  const createValidationSchema = useCallback((sections: SectionConfig[], currentFormValues: Record<string, unknown> = {}) => {
    const schemaObj: Record<string, z.ZodTypeAny> = {};

    // Check if this form has any dynamic behavior (visibility/required/disabled rules or events)
    const hasDynamicBehavior = sections.some(section => 
      section.fields.some(field => 
        Boolean(
          field.visibilityCondition ||
          field.visibleWhen !== undefined ||
          field.requiredWhen !== undefined ||
          field.disabledWhen !== undefined ||
          (field.events && (
            (field.events.onChange && field.events.onChange.length > 0) ||
            (field.events.onBlur && field.events.onBlur.length > 0) ||
            (field.events.onMount && field.events.onMount.length > 0)
          ))
        )
      )
    );

    // Generate dynamic fields for validation
    const generateFieldsForValidation = () => {
      const allFields: FieldConfig[] = [];
      
      sections.forEach(section => {
        section.fields.forEach(field => {
          if (field.dataType === 'date-with-clear') {
            // Check if this is a dependent DOB field that should be repeated
            if (field.fieldName === 'under16DependentsDOB') {
              const under16Count = Number(currentFormValues.under16Dependents || 0);
              for (let i = 1; i <= under16Count; i++) {
                const dynamicField: FieldConfig = {
                  ...field,
                  fieldName: `under16DependentsDOB_${i}`,
                  label: `Dependent Child ${i} Date Of Birth`,
                };
                allFields.push(dynamicField);
              }
            } else if (field.fieldName === 'between16to18DependentsDOB') {
              const between16to18Count = Number(currentFormValues.between16to18Dependents || 0);
              for (let i = 1; i <= between16to18Count; i++) {
                const dynamicField: FieldConfig = {
                  ...field,
                  fieldName: `between16to18DependentsDOB_${i}`,
                  label: `Dependent Child ${i} Date Of Birth`,
                };
                allFields.push(dynamicField);
              }
            } else {
              // Regular date field, add as is
              allFields.push(field);
            }
          } else {
            // Non-date fields, add as is
            allFields.push(field);
          }
        });
      });
      
      return allFields;
    };

    const fieldsToValidate = generateFieldsForValidation();

    fieldsToValidate.forEach(field => {
      const isFieldDisabled = hasDynamicBehavior ? computeIsDisabled(field, currentFormValues) : (field.isReadOnly || !field.isEditable);
      const isFieldVisible = hasDynamicBehavior ? computeIsVisible(field, currentFormValues) : !field.isHidden;
      
      let fieldSchema: z.ZodTypeAny;

      const shouldBeRequired = computeShouldBeRequired(field, hasDynamicBehavior, currentFormValues);
      

      switch (field.dataType) {
        case 'separator': {
          // Separator is a UI-only element; never required and produces no value
          fieldSchema = z.any().optional();
          break;
        }
        case 'special-text':
        case 'text':
        case 'email':
        case 'password':
        case 'tel':
        case 'textarea': {
          let stringSchema = z.string().trim();
          // Apply required validation - use dynamic logic only if form has dynamic behavior
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            stringSchema = stringSchema.min(1, {
              message: field.requiredErrorMessage || `${field.label} is required`
            });
          }
          if (field.validationRules) {
            field.validationRules.forEach(rule => {
              switch (rule.type) {
                case 'minLength':
                  if (typeof rule.value === 'number') {
                    stringSchema = stringSchema.min(rule.value, rule.message || `${field.label} must be at least ${rule.value} characters`);
                  }
                  break;
                case 'maxLength':
                  if (typeof rule.value === 'number') {
                    stringSchema = stringSchema.max(rule.value, rule.message || `${field.label} must be at most ${rule.value} characters`);
                  }
                  break;
                case 'pattern':
                  if (typeof rule.value === 'string') {
                    // Make pattern optional: allow empty string OR the provided pattern
                    // Wrap original pattern in a non-capturing optional group anchored to full string
                    const optionalWholeStringPattern = `^(?:${rule.value})?$`;
                    const regex = rule.flags
                      ? new RegExp(optionalWholeStringPattern, rule.flags)
                      : new RegExp(optionalWholeStringPattern);
                    stringSchema = stringSchema.regex(regex, rule.message || 'Invalid format');
                  }
                  break;
              }
            });
          }
          fieldSchema = shouldBeRequired
            ? stringSchema
            : z.union([stringSchema, z.literal('')]).optional();
          
          // Add checkbox field to schema if allowNoValue is enabled
          if (field.ui?.allowNoValue && field.ui?.noValueFieldName) {
            const checkboxFieldName = field.ui.noValueFieldName as string;
            schemaObj[checkboxFieldName] = z.boolean().optional();
          }
          break;
        }

        case 'number': {
          let numberSchema = z.number();
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            numberSchema = numberSchema.min(0, {
              message: field.requiredErrorMessage || `${field.label} is required`
            });
          }
          if (field.validationRules) {
            field.validationRules.forEach(rule => {
              switch (rule.type) {
                case 'min':
                  if (typeof rule.value === 'number') {
                    numberSchema = numberSchema.min(rule.value, rule.message || `${field.label} must be at least ${rule.value}`);
                  }
                  break;
                case 'max':
                  if (typeof rule.value === 'number') {
                    numberSchema = numberSchema.max(rule.value, rule.message || `${field.label} must be at most ${rule.value}`);
                  }
                  break;
              }
            });
          }
          fieldSchema = shouldBeRequired ? numberSchema : numberSchema.optional();
          break;
        }

        case 'boolean-check':
          fieldSchema = z.boolean();
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            fieldSchema = fieldSchema.refine(val => val === true, {
              message: field.requiredErrorMessage || `${field.label} is required`
            });
          } else {
            fieldSchema = fieldSchema.optional();
          }
          break;

        case 'select':
        case 'multiselect': {
          const requiredMessage = field.requiredErrorMessage || `${field.label} is required`;
          fieldSchema = field.dataType === 'multiselect' 
            ? z.array(z.string(), { required_error: requiredMessage, invalid_type_error: requiredMessage }) 
            : z.string({ required_error: requiredMessage });
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            fieldSchema = fieldSchema.refine(val => (Array.isArray(val) ? val.length > 0 : val && val.length > 0), {
              message: requiredMessage
            });
          } else {
            fieldSchema = (fieldSchema as z.ZodTypeAny).optional();
          }
          break;
        }

        case 'checkbox': {
          // Checkbox can be a group (with options) or a single boolean toggle
          const hasOptions = Array.isArray(field.options) && field.options.length > 0;
          if (hasOptions) {
            // Group: array of strings with optional min/max selections and allowed values guard
            const requiredMessage = field.requiredErrorMessage || `${field.label} is required`;
            let arraySchema = z.array(z.string(), {
              required_error: requiredMessage,
              invalid_type_error: requiredMessage
            });

            // Apply min/max selections from validation rules if provided
            let minSel: number | undefined;
            let minSelMsg: string | undefined;
            let maxSel: number | undefined;
            let maxSelMsg: string | undefined;
            if (field.validationRules) {
              field.validationRules.forEach(rule => {
                switch (rule.type) {
                  case 'minSelections':
                    if (typeof rule.value === 'number') {
                      minSel = rule.value;
                      minSelMsg = rule.message || `Please select at least ${rule.value} option${rule.value > 1 ? 's' : ''}`;
                    }
                    break;
                  case 'maxSelections':
                    if (typeof rule.value === 'number') {
                      maxSel = rule.value;
                      maxSelMsg = rule.message || `Please select at most ${rule.value} option${rule.value > 1 ? 's' : ''}`;
                    }
                    break;
                }
              });
            }

            // If required, ensure at least 1 (or respect higher min from rules)
            if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
              const effectiveMin = Math.max(1, minSel ?? 0);
              arraySchema = arraySchema.min(effectiveMin, { message: minSel ? (minSelMsg as string) : requiredMessage });
            } else if (minSel !== undefined) {
              arraySchema = arraySchema.min(minSel, { message: minSelMsg as string });
            }
            if (maxSel !== undefined) {
              arraySchema = arraySchema.max(maxSel, { message: maxSelMsg as string });
            }

            // Constrain to allowed option values (after min/max so we don't call min on effects)
            const allowedValues = (field.options || [])
              .map((opt) => String((opt as any).value))
              .filter((v) => Boolean(v));
            const constrained = arraySchema.refine(
              (vals) => Array.isArray(vals) && vals.every(v => allowedValues.includes(String(v))),
              { message: field.requiredErrorMessage || 'Invalid selection' }
            );

            fieldSchema = shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
              ? constrained
              : constrained.optional();
          } else {
            // Single checkbox: boolean required -> must be true
            let boolSchema: z.ZodTypeAny = z.boolean();
            if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
              boolSchema = boolSchema.refine(val => val === true, {
                message: field.requiredErrorMessage || `${field.label} is required`
              });
            } else {
              boolSchema = boolSchema.optional();
            }
            fieldSchema = boolSchema;
          }
          break;
        }

        case 'radiogroup': {
          const requiredMessage = field.requiredErrorMessage || `${field.label} is required`;
          // Limit to allowed option values to avoid stale values from previous steps (e.g. 'yes')
          const allowedValues = (field.options || [])
            .map((opt) => String((opt as any).value))
            .filter((v) => Boolean(v));

          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            // Require non-empty and that the value is one of the allowed options
            fieldSchema = z
              .string({ required_error: requiredMessage })
              .min(1, { message: requiredMessage })
              .refine((val) => allowedValues.length === 0 || allowedValues.includes(String(val)), {
                message: requiredMessage,
              });
          } else {
            // Not required: allow undefined/empty. If provided, it should match an allowed option
            fieldSchema = z
              .string()
              .optional()
              .refine(
                (val) => val === undefined || val === '' || allowedValues.length === 0 || allowedValues.includes(String(val)),
                { message: field.requiredErrorMessage || 'Invalid selection' }
              );
          }
          break;
        }

        case 'date':
        case 'time':
        case 'datetime': {
          // Build a robust date schema that distinguishes between required and invalid values
          const requiredMessage = field.requiredErrorMessage || `${field.label} is required`;
          const invalidDateMessage = 'Please enter a valid date';

          // For required fields, convert null -> undefined so required_error is shown instead of invalid type
          const buildBaseDateSchema = (isRequired: boolean) => {
            if (isRequired) {
              return z.preprocess(
                (val) => (val === null ? undefined : val),
                z.date({ required_error: requiredMessage, invalid_type_error: invalidDateMessage })
              );
            }
            // Optional field: allow undefined; still show invalid message when an invalid date object is present
            return z.preprocess(
              (val) => (val === null ? undefined : val),
              z.date({ invalid_type_error: invalidDateMessage }).optional()
            );
          };

          let dateSchema: z.ZodTypeAny = buildBaseDateSchema(
            shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
          );

          // Apply age constraints from validationRules, if provided
          if (Array.isArray(field.validationRules) && field.validationRules.length > 0) {
            let minAge: number | undefined;
            let minAgeMsg: string | undefined;
            let maxAge: number | undefined;
            let maxAgeMsg: string | undefined;

            field.validationRules.forEach((rule) => {
              switch (rule.type) {
                case 'minAge':
                  if (typeof rule.value === 'number') {
                    minAge = rule.value;
                    minAgeMsg = rule.message || `Must be at least ${rule.value} years old`;
                  }
                  break;
                case 'maxAge':
                  if (typeof rule.value === 'number') {
                    maxAge = rule.value;
                    maxAgeMsg = rule.message || `Must be no more than ${rule.value} years old`;
                  }
                  break;
              }
            });

            if (typeof minAge === 'number' && Number.isFinite(minAge)) {
              dateSchema = dateSchema.refine((d: unknown) => {
                if (!(d instanceof Date)) return true; // Type error handled earlier
                const today = new Date();
                const ageYears = calculateAge(d, today);
                return typeof ageYears === 'number' && ageYears >= (minAge as number);
              }, { message: minAgeMsg as string });
            }

            if (typeof maxAge === 'number' && Number.isFinite(maxAge)) {
              dateSchema = dateSchema.refine((d: unknown) => {
                if (!(d instanceof Date)) return true; // Type error handled earlier
                const today = new Date();
                const ageYears = calculateAge(d, today);
                return typeof ageYears === 'number' && ageYears <= (maxAge as number);
              }, { message: maxAgeMsg as string });
            }
          }

          fieldSchema = dateSchema as z.ZodTypeAny;
          break;
        }

        case 'radio': {
          let stringSchema = z.string();
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            stringSchema = stringSchema.min(1, {
              message: field.requiredErrorMessage || `${field.label} is required`
            });
          }
          fieldSchema = shouldBeRequired ? stringSchema : stringSchema.optional();
          break;
        }

        case 'toggle-button': {
          let booleanSchema = z.boolean();
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            // For toggle-button, we require a boolean value (true or false)
            fieldSchema = booleanSchema;
          } else {
            fieldSchema = booleanSchema.optional();
          }
          break;
        }

        case 'only-label': {
          // Only-label fields don't have form values, so we skip validation
          fieldSchema = z.any().optional();
          break;
        }

        case 'counter': {
          let numberSchema = z.number().int().min(0);
          if (shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))) {
            numberSchema = numberSchema.min(1, {
              message: field.requiredErrorMessage || `${field.label} is required`
            });
          }
          fieldSchema = shouldBeRequired ? numberSchema : numberSchema.optional();
          break;
        }

        case 'date-with-clear': {
          // Build a robust date schema that distinguishes between required and invalid values
          const requiredMessage = field.requiredErrorMessage || `${field.label} is required`;
          const invalidDateMessage = 'Please enter a valid date';

          // For required fields, convert null -> undefined so required_error is shown instead of invalid type
          const buildBaseDateSchema = (isRequired: boolean) => {
            if (isRequired) {
              return z.preprocess(
                (val) => (val === null ? undefined : val),
                z.date({ required_error: requiredMessage, invalid_type_error: invalidDateMessage })
              );
            }
            // Optional field: allow undefined; still show invalid message when an invalid date object is present
            return z.preprocess(
              (val) => (val === null ? undefined : val),
              z.date({ invalid_type_error: invalidDateMessage }).optional()
            );
          };

          let dateSchema: z.ZodTypeAny = buildBaseDateSchema(
            shouldBeRequired && (!hasDynamicBehavior || (!isFieldDisabled && isFieldVisible))
          );

          // Apply age constraints from validationRules, if provided
          if (Array.isArray(field.validationRules) && field.validationRules.length > 0) {
            let minAge: number | undefined;
            let minAgeMsg: string | undefined;
            let maxAge: number | undefined;
            let maxAgeMsg: string | undefined;

            field.validationRules.forEach((rule) => {
              switch (rule.type) {
                case 'minAge':
                  if (typeof rule.value === 'number') {
                    minAge = rule.value;
                    minAgeMsg = rule.message || `Must be at least ${rule.value} years old`;
                  }
                  break;
                case 'maxAge':
                  if (typeof rule.value === 'number') {
                    maxAge = rule.value;
                    maxAgeMsg = rule.message || `Must be no more than ${rule.value} years old`;
                  }
                  break;
              }
            });

            if (typeof minAge === 'number' && Number.isFinite(minAge)) {
              dateSchema = dateSchema.refine((d: unknown) => {
                if (!(d instanceof Date)) return true; // Type error handled earlier
                const today = new Date();
                const ageYears = calculateAge(d, today);
                return typeof ageYears === 'number' && ageYears >= (minAge as number);
              }, { message: minAgeMsg as string });
            }

            if (typeof maxAge === 'number' && Number.isFinite(maxAge)) {
              dateSchema = dateSchema.refine((d: unknown) => {
                if (!(d instanceof Date)) return true; // Type error handled earlier
                const today = new Date();
                const ageYears = calculateAge(d, today);
                return typeof ageYears === 'number' && ageYears <= (maxAge as number);
              }, { message: maxAgeMsg as string });
            }
          }

          fieldSchema = dateSchema as z.ZodTypeAny;
          break;
        }

        case 'count-label': {
          // Count-label fields are display-only, so we skip validation
          fieldSchema = z.any().optional();
          break;
        }

        default:
          fieldSchema = z.any();
      }

      schemaObj[field.fieldName] = fieldSchema;
    });

    return z.object(schemaObj);
  }, [parseVisibilityCondition]);

  // Check if this form needs dynamic validation
  const needsDynamicValidation = useMemo(() => 
    schema.sections.some(section => 
      section.fields.some(field => 
        Boolean(
          field.visibilityCondition ||
          field.visibleWhen !== undefined ||
          field.requiredWhen !== undefined ||
          field.disabledWhen !== undefined ||
          (field.events && (
            (field.events.onChange && field.events.onChange.length > 0) ||
            (field.events.onBlur && field.events.onBlur.length > 0) ||
            (field.events.onMount && field.events.onMount.length > 0)
          ))
        )
      )
    ), [schema.sections]
  );

  // Create initial validation schema
  const initialValidationSchema = useMemo(() => 
    createValidationSchema(schema.sections), 
    [schema.sections, createValidationSchema]
  );

  const resolverRef = useRef<Resolver<FormData>>(zodResolver(initialValidationSchema));
  const resolver = useCallback<Resolver<FormData>>((values, context, options) => {
    return resolverRef.current(values, context, options);
  }, []);

  // Create default values from schema and merge with initial data
  const createDefaultValues = useCallback((sections: SectionConfig[]) => {
    const defaults: Record<string, unknown> = {};
    
    sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          defaults[field.fieldName] = field.defaultValue;
        }
      });
    });
    
    return defaults;
  }, []);

  const schemaDefaults = useMemo(() => createDefaultValues(schema.sections), [schema.sections, createDefaultValues]);
  const formDefaults = useMemo(() => ({ ...schemaDefaults, ...initialData }), [schemaDefaults, initialData]);

  // Use ref to track formDefaults without triggering effects
  const formDefaultsRef = useRef(formDefaults);
  useEffect(() => {
    formDefaultsRef.current = formDefaults;
  }, [formDefaults]);

  const form = useForm<FormData>({
    resolver,
    defaultValues: formDefaults,
  });

  // Track initialized checkbox fields for special-text allowNoValue feature
  const initializedCheckboxFields = useRef<Set<string>>(new Set());
  // Throttle blocked-input alert for referenceId/referenceNumber/accountNumber fields
  const lastApostropheAlertAtRef = useRef<number>(0);
  const isAccountField = useCallback((name: string): boolean => {
    if (!name) return false;
    const isBase = name === 'referenceId' || name === 'referenceNumber' || name === 'accountNumber';
    if (isBase) return true;
    return name.endsWith('.referenceId') || name.endsWith('.referenceNumber') || name.endsWith('.accountNumber');
  }, []);

  const triggerBlockedInputAlert = useCallback((isOverLimit?: boolean) => {
    const now = Date.now();
    if (now - lastApostropheAlertAtRef.current < 1500) {
      return;
    }
    lastApostropheAlertAtRef.current = now;
    const message = isOverLimit
      ? UI_MESSAGES.CHARACTER_LIMIT_REACHED
      : UI_MESSAGES.APOSTROPHE_NOT_ALLOWED;

    try {
      window.dispatchEvent(
        new CustomEvent('iehub-alert', {
          detail: {
            severity: 'warning',
            message: message,
            variant: 'filled',
            duration: 3000,
          },
        })
      );
    } catch {
      // no-op if dispatch fails
    }
  }, []);

  // Local tick to force re-render when form values change (ensures visibleWhen/disabledWhen re-evaluation)
  // Local state tick to force re-render when form values change (ensures visibleWhen/requiredWhen updates)
  const [, forceRender] = useState(0);

  // Subscribe to form value changes: trigger re-render and bubble up values when provided
  useEffect(() => {
    const subscription = (form as any).watch((value: Record<string, unknown>) => {
      // Force this component to re-render so visibility/disabled rules recalculate immediately
      forceRender((prev) => prev + 1);
      
      if (onValuesChange) {
        try {
          onValuesChange(value as Record<string, unknown>);
        } catch {
          // no-op
        }
      }
    });
    return () => {
      if (subscription && typeof (subscription as any).unsubscribe === 'function') {
        (subscription as any).unsubscribe();
      }
    };
  }, [form, onValuesChange, schema.sections, computeIsVisible]);

  const triggerFieldEvents = useCallback(async (eventName: keyof FieldEvents, field: FieldConfig, newValue: unknown) => {
    const groups = field.events?.[eventName];
    if (!groups || groups.length === 0) return;

    const currentValues = form.getValues ? form.getValues() : ({} as Record<string, unknown>);
    const context = { ...currentValues, $value: newValue, $field: field.fieldName } as Record<string, unknown>;

    for (const group of groups) {
      const shouldRun = group.when ? evaluateRule(group.when, context) : true;
      if (!shouldRun) continue;
      await runEffects(group.actions, {
        navigate,
        formApi: {
          setValue: (name, value, options) => form.setValue(name as string, value as unknown, options as any),
          reset: (values, options) => form.reset(values as any, options as any)
        }
      });
    }
  }, [evaluateRule, form, navigate]);

  // Ensure default values are applied when schema changes
  useEffect(() => {
    // Only reset form if the schema actually changed, not just the initial data
    const currentValues = form.getValues();
    const defaults = formDefaultsRef.current;
    const hasExistingData = Object.keys(currentValues).some(key => 
      currentValues[key] !== undefined && currentValues[key] !== '' && currentValues[key] !== null
    );
    
    // Only reset if there's no existing data or if the schema structure changed
    if (!hasExistingData) {
      form.reset(defaults);
    } else {
      // Update only the fields that don't have values yet
      Object.keys(defaults).forEach(key => {
        if (currentValues[key] === undefined || currentValues[key] === '' || currentValues[key] === null) {
          form.setValue(key, defaults[key]);
        }
      });
    }
  }, [form, schema.formName, schema.version]);

  // Watch form values only if dynamic validation is needed
  const formValues = needsDynamicValidation ? form.watch() : {};
  const validationSchema = useMemo(() => {
    if (needsDynamicValidation) {
      return createValidationSchema(schema.sections, formValues);
    }
    return initialValidationSchema;
  }, [schema.sections, createValidationSchema, formValues, needsDynamicValidation, initialValidationSchema]);

  useEffect(() => {
    resolverRef.current = zodResolver(validationSchema) as Resolver<FormData>;
  }, [validationSchema]);

  const handleSubmit = useCallback(async (data: FormData) => {
    setLoading(true);
    try {
      // Always validate against the current schema to ensure correctness across step changes
      const result = validationSchema.safeParse(data);
      if (!result.success) {
        // Set validation errors
        const errors: Record<string, { message: string }> = {};
        result.error.errors.forEach((error) => {
          if (error.path.length > 0) {
            errors[error.path[0] as string] = { message: error.message };
          }
        });
        // Apply errors to form controls
        Object.entries(errors).forEach(([field, error]) => {
          form.setError(field, error);
        });
        setLoading(false);
        return;
      }
      
      // Clean up the data before submission
      const cleanedData: Record<string, unknown> = { ...data };

      // Strip values for fields that are currently hidden so they don't leak into payloads
      const currentFormValues = form.getValues() as Record<string, unknown>;
      schema.sections.forEach((section) => {
        section.fields.forEach((field) => {
          const isVisible = computeIsVisible(field, currentFormValues);
          if (!isVisible && Object.prototype.hasOwnProperty.call(cleanedData, field.fieldName)) {
            delete cleanedData[field.fieldName];
          }
        });
      });
      
      // If "Not Referred By a Company" is checked, ensure company field is cleared
      if (cleanedData.notReferredByCompany === true && typeof cleanedData.referedFromCompany === 'string') {
        cleanedData.referedFromCompany = '';
      }
      
      if (onSubmit) {
        await onSubmit(cleanedData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  }, [onSubmit, validationSchema, form, needsDynamicValidation]);

  const renderPrivacyHelper = useCallback((text?: string): React.ReactNode => {
    if (!text) return undefined;
    const parts = text.split(/(Privacy\s*Policy)/i);
    if (parts.length === 1) return text;
    return (
      <>
        {parts.map((part, idx) => {
          if (/privacy\s*policy/i.test(part)) {
            return (
              <Link
                key={`pp-${idx}`}
                onClick={() => setPrivacyOpen(true)}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Privacy Policy
              </Link>
            );
          }
          return <span key={`txt-${idx}`}>{part}</span>;
        })}
      </>
    );
  }, []);

  // Reactive CountLabel component that watches form values
  const ReactiveCountLabel = ({ field }: { field: FieldConfig }) => {
    // Watch the specific fields that the expression depends on
    const under16Dependents = useWatch({ control: form.control, name: 'under16Dependents' }) || 0;
    const between16to18Dependents = useWatch({ control: form.control, name: 'between16to18Dependents' }) || 0;
    const otherAdultsOver18 = useWatch({ control: form.control, name: 'otherAdultsOver18' }) || 0;
    const budgetWithPartner = useWatch({ control: form.control, name: 'budgetWithPartner' });
    const jointAccount = useWatch({ control: form.control, name: 'jointAccount' });
    
    const calculateCountValue = () => {
      if (field.valueExpression) {
        try {
          const formValues = {
            under16Dependents: Number(under16Dependents),
            between16to18Dependents: Number(between16to18Dependents),
            otherAdultsOver18: Number(otherAdultsOver18),
            budgetWithPartner,
            jointAccount:
              typeof jointAccount === 'string'
                ? jointAccount
                : budgetWithPartner
                  ? 'Y'
                  : 'N',
          };
          const result = evaluateNode(field.valueExpression, formValues);
          const num = typeof result === 'number' ? result : Number(result);
          return Number.isFinite(num) ? num : 0;
        } catch {
          return 0;
        }
      }
      return 0;
    };
    
    return (
      <CountLabel
        key={field.fieldName}
        label={field.label}
        value={calculateCountValue()}
      />
    );
  };

  const renderField = useCallback((field: FieldConfig, suppressFieldError: boolean = false) => {
    // Skip hidden fields
    if (field.isHidden) return null;

    const error = form.formState.errors[field.fieldName];
    // Normalize error messages (e.g., any variant of invalid date)
    const normalizeErrorMessage = (msg?: string): string | undefined => {
      if (typeof msg !== 'string') return undefined;
      const lower = msg.trim().toLowerCase();
      if (lower === 'invalid date' || lower === 'invaliddate' || (lower.includes('invalid') && lower.includes('date'))) {
        return 'Please enter a valid date';
      }
      return msg;
    };
    const normalizedErrorMessage = normalizeErrorMessage(error?.message as string | undefined);
    const fieldValue = form.watch(field.fieldName);
    const allFormValues = form.watch();
    
    // Check if field should be disabled based on other field values
    const isFieldDisabled = computeIsDisabled(field, allFormValues);
    
    // Don't show errors for disabled fields or when section-level error display is enabled
    const shouldShowError = !!error && !isFieldDisabled && !suppressFieldError;

    // Build validation rules from field config
    const validationRules: any = {};
    if (field.isRequired) {
      validationRules.required = true;
    }
    if (field.validationRules) {
      field.validationRules.forEach(rule => {
        switch (rule.type) {
          case 'minLength':
            validationRules.minLength = rule.value;
            break;
          case 'maxLength':
            validationRules.maxLength = rule.value;
            break;
          case 'pattern':
            validationRules.pattern = rule.value;
            validationRules.patternMessage = rule.message;
            if ((rule as any).flags) {
              (validationRules as any).patternFlags = (rule as any).flags;
            }
            break;
          case 'minSelections':
            validationRules.minSelections = rule.value;
            break;
          case 'maxSelections':
            validationRules.maxSelections = rule.value;
            break;
          case 'minAge':
            validationRules.minAge = rule.value;
            break;
          case 'maxAge':
            validationRules.maxAge = rule.value;
            break;
        }
      });
    }

    switch (field.dataType) {
      case 'separator': {
        const variant = field.ui?.separatorVariant || 'solid';
        const thickness = field.ui?.separatorThickness ?? 1;
        const color = field.ui?.separatorColor || theme.palette.divider;
        const spacingY = field.ui?.separatorSpacingY ?? 2;
        return (
          <Box
            key={field.fieldName}
            sx={{
              my: spacingY,
              width: '100%'
            }}
          >
            <Box
              sx={{
                width: '100%',
                borderTopWidth: thickness,
                borderTopStyle: variant === 'dashed' ? 'dashed' : 'solid',
                borderTopColor: color
              }}
            />
          </Box>
        );
      }
      
      case 'special-text':
        {
          const registered = form.register(field.fieldName);
          // Get checkbox field name and state if allowNoValue is enabled
          const noValueFieldName = field.ui?.noValueFieldName as string | undefined;
          // Initialize the checkbox field value if it exists and hasn't been initialized yet
          if (noValueFieldName && !initializedCheckboxFields.current.has(noValueFieldName)) {
            const currentValue = form.getValues(noValueFieldName);
            if (currentValue === undefined) {
              form.setValue(noValueFieldName, false, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
            }
            initializedCheckboxFields.current.add(noValueFieldName);
          }
          const noValueChecked = noValueFieldName ? form.watch(noValueFieldName) as boolean : false;
          
          // Don't show errors when "no value" checkbox is checked
          const actualShouldShowError = noValueChecked ? false : shouldShowError;
          
          const referenceHelp = resolveReferenceHelp(
            field.fieldName,
            allFormValues,
            field.ui?.referenceHelp
          );
          const helpDialog = referenceHelp.helpDialog ?? field.ui?.helpDialog;
          const helpLinkText = referenceHelp.helpLinkText ?? field.ui?.helpLinkText;

          // Build optional props for noValue checkbox feature
          const noValueProps: any = {};
          if (field.ui?.allowNoValue) {
            noValueProps.allowNoValue = true;
            // Use dynamic checkbox label from reference help, then field config, then default
            noValueProps.noValueCheckboxLabel = referenceHelp.noValueCheckboxLabel ?? field.ui.noValueCheckboxLabel ?? "I don't have this information";
            if (noValueFieldName) {
              noValueProps.noValueFieldName = noValueFieldName;
              noValueProps.noValueChecked = noValueChecked;
              noValueProps.onNoValueChange = (checked: boolean) => {
                form.setValue(noValueFieldName, checked, { shouldValidate: false });
                // Clear the text field and error when checkbox is checked
                if (checked) {
                  form.setValue(field.fieldName, '', { shouldValidate: false });
                  form.clearErrors(field.fieldName);
                } else {
                  // When unchecked, re-validate the field
                  form.trigger(field.fieldName);
                }
              };
            }
          }

          return (
            <SpecialTextField
              key={field.fieldName}
              {...registered}
              onChange={(e: any) => {
                const originalValue = e?.target?.value as string | undefined;
                const isAccount = isAccountField(field.fieldName);
                if (isAccount && typeof originalValue === 'string') {
                  const isOverLimit = originalValue.length > 50;
                  const hasApostrophe = originalValue.includes("'");

                  if (isOverLimit || hasApostrophe) {
                    triggerBlockedInputAlert(isOverLimit);
                    const sanitized = originalValue.replace(/'/g, '').slice(0, 50);
                    const syntheticEvent = {
                      ...e,
                      target: {
                        ...e.target,
                        value: sanitized,
                      },
                    } as any;
                    registered.onChange(syntheticEvent);
                    triggerFieldEvents('onChange', field, sanitized);
                    return;
                  }
                }
                registered.onChange(e);
                triggerFieldEvents('onChange', field, originalValue);
              }}
              type="text"
              label={field.label}
              placeholder={field.ui?.placeholder || field.helpText || ''}
              disabled={isFieldDisabled}
              error={actualShouldShowError}
              helperText={(actualShouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
              validation={validationRules}
              deferValidation
              showCharacterCount={!!field.maxLength}
              showValidationIcon
              inputProps={{
                maxLength: field.maxLength ?? (isAccountField(field.fieldName) ? 50 : undefined),
                onKeyDown: (ke: any) => {
                  if (isAccountField(field.fieldName) && ke?.key === "'") {
                    ke.preventDefault();
                    triggerBlockedInputAlert(false);
                  }
                },
                onPaste: (pe: any) => {
                  const maxLength = field.maxLength ?? (isAccountField(field.fieldName) ? 50 : undefined);
                  if (maxLength) {
                    const pastedText = pe.clipboardData.getData('text');
                    const selectionStart = pe.target.selectionStart || 0;
                    const selectionEnd = pe.target.selectionEnd || 0;
                    const currentLength = pe.target.value.length;
                    if (currentLength - (selectionEnd - selectionStart) + pastedText.length > maxLength) {
                      triggerBlockedInputAlert(true);
                    }
                  }
                }
              }}
              fullWidth
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              helpDialog={helpDialog}
              helpLinkText={helpLinkText}
              dataPrivate={field.dataPrivate ?? true}
              {...noValueProps}
            />
          );
        }

      case 'text':
      case 'email':
      case 'tel':
      case 'password':
      case 'textarea':
        {
          const registered = form.register(field.fieldName);
          
          return (
            <TextField
              key={field.fieldName}
              {...registered}
              onChange={(e: any) => {
                registered.onChange(e);
                triggerFieldEvents('onChange', field, e?.target?.value);
              }}
              type={field.dataType === 'textarea' ? 'text' : field.dataType}
              label={field.label ?? ''}
              placeholder={field.ui?.placeholder || field.helpText || ''}
              disabled={isFieldDisabled}
              error={shouldShowError}
              helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText ?? undefined)}
              validation={validationRules}
              deferValidation
              showPasswordToggle={field.dataType === 'password'}
              showCharacterCount={!!field.maxLength}
              showValidationIcon
              inputProps={{ maxLength: field.maxLength ?? undefined }}
              fullWidth
              helperTop={field.ui?.helperTop}
              helperAttached={field.ui?.helperAttached}
              helperBottom={field.ui?.helperBottom}
              showErrorBelow={field.ui?.showErrorBelow ?? true}
              multiline={field.dataType === 'textarea'}
              rows={field.dataType === 'textarea' ? (4 as number | string) : (undefined as unknown as number | string)}
              maxRows={field.dataType === 'textarea' ? (8 as number | string) : (undefined as unknown as number | string)}
              dataPrivate={field.dataPrivate ?? true}
            />
          );
        }

      case 'select':
      case 'multiselect':
        return (
          <Select
            key={field.fieldName}
            label={field.label ?? ''}
            labelVariant={field.ui?.labelVariant}
            dialogLabel={(processDynamicValue(field.ui?.dialogLabel, allFormValues) as string) || ''}
            options={field.options || []}
            value={fieldValue !== undefined ? fieldValue : (field.dataType === 'multiselect' ? [] : '') as any}
            onChange={(value) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            multiple={field.dataType === 'multiselect'}
            disabled={isFieldDisabled}
            placeholder={field.ui?.placeholder || 'Select an option'}
            helperTop={field.ui?.helperTop}
            helperAttached={field.ui?.helperAttached}
            helperBottom={field.ui?.helperBottom}
            helperBottomTone={field.ui?.helperBottomTone}
            showErrorBelow={field.ui?.showErrorBelow ?? true}
            searchable={field.ui?.searchable ?? true}
            allowCustomEntry={Boolean(field.ui?.allowCustomEntry)}
            customEntryLinkText={field.ui?.customEntryLinkText ?? 'Can\'t find my referring company'}
            customEntryPlaceholder={field.ui?.customEntryPlaceholder ?? 'Type your company name'}
            customEntryConfirmText={field.ui?.customEntryConfirmText ?? 'Use this company'}
            customEntryCancelText={field.ui?.customEntryCancelText ?? 'Back to list'}
            showValidationIcon
            fullWidth
            deferValidation
            dataPrivate={field.dataPrivate ?? true}
          />
        );

      case 'boolean-check':
        return (
          <Box
            key={field.fieldName}
            sx={{
              border: '2px solid',
              borderColor: fieldValue ? 'primary.main' : 'grey.300',
              borderRadius: '14px',
              padding: '6px 14px',
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: fieldValue ? 'primary.50' : 'transparent',
              '&:hover': {
                borderColor: fieldValue ? 'primary.dark' : 'grey.400',
                backgroundColor: fieldValue ? 'primary.100' : 'grey.50',
              },
              ...(isFieldDisabled && {
                opacity: 0.6,
                cursor: 'not-allowed',
              }),
              ...(shouldShowError && {
                borderColor: 'error.main',
                backgroundColor: 'error.50',
              }),
            }}
            onClick={() => {
              if (!isFieldDisabled) {
                const newValue = !fieldValue;
                form.setValue(field.fieldName, newValue, { shouldValidate: true });
                triggerFieldEvents('onChange', field, newValue);
              }
            }}
          >
            <Checkbox
              type="checkbox"
              value={fieldValue || false}
              onChange={() => {}} // Handled by container click
              disabled={isFieldDisabled}
              icon={<CheckBoxOutlineBlankRoundedIcon />}
              checkedIcon={<CheckBoxRoundedIcon />}
              sx={{
                padding: 0,
                marginRight: 1,
                mb: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: '18px',
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: isFieldDisabled ? 'text.disabled' : shouldShowError ? 'error.main' : 'primary.main',
                fontWeight: 500,
                userSelect: 'none',
              }}
            >
              {field.label}
            </Typography>
            {shouldShowError && (
              <FormHelperText 
                error 
                sx={{ 
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  mt: 0.5,
                }}
              >
                {error?.message as string}
              </FormHelperText>
            )}
          </Box>
        );

      case 'checkbox': {
        if (field.options && field.options.length > 0) {
          return (
            <CheckboxGroup
              key={field.fieldName}
              label={field.label}
              value={Array.isArray(fieldValue) ? (fieldValue as Array<string | number>) : []}
              onChange={(vals: Array<string | number>) => {
                form.setValue(field.fieldName, vals, { shouldValidate: true });
                triggerFieldEvents('onChange', field, vals);
              }}
              options={(field.options || []).map(opt => ({
                value: opt.value,
                title: (opt as any).title || opt.label,
                description: (opt as any).description,
                infoHtml: (opt as any).infoHtml
              }))}
              error={shouldShowError}
              helperText={(shouldShowError ? (error?.message as string) : undefined) || (field.helpText as string | undefined)}
              validation={validationRules}
              disabled={isFieldDisabled}
              variant="outlined"
            />
          );
        }
        return (
          <Checkbox
            key={field.fieldName}
            type="checkbox"
            label={field.label}
            value={fieldValue || false}
            onChange={(checked: boolean) => {
              form.setValue(field.fieldName, checked, { shouldValidate: true });
              triggerFieldEvents('onChange', field, checked);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            variant="soft"
          />
        );
      }

      case 'radio':
        return (
          <Checkbox
            key={field.fieldName}
            type="radio"
            groupLabel={field.label}
            value={(fieldValue as string) || ''}
            onChange={(val: string) => {
              form.setValue(field.fieldName, val, { shouldValidate: true });
              triggerFieldEvents('onChange', field, val);
            }}
            options={(field.options || []).map(opt => ({ value: opt.value, label: opt.label }))}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            variant="outlined"
          />
        );

      case 'switch':
        return (
          <Checkbox
            key={field.fieldName}
            type="switch"
            label={field.label}
            value={Boolean(fieldValue)}
            onChange={(checked: boolean) => {
              form.setValue(field.fieldName, checked, { shouldValidate: true });
              triggerFieldEvents('onChange', field, checked);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            variant="soft"
          />
        );

      case 'toggle-button':
        return (
          <ToggleButton
            key={field.fieldName}
            label={field.label}
            value={fieldValue as boolean | undefined}
            onChange={(value: boolean) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
          />
        );

      case 'only-label':
        return (
          <Typography
            key={field.fieldName}
            variant="body1"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              mb: 1,
              fontSize: '0.875rem'
            }}
          >
            {field.label}
          </Typography>
        );

      case 'counter':
        return (
          <Counter
            key={field.fieldName}
            label={field.label}
            value={fieldValue as number || 0}
            onChange={(value: number) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            min={typeof field.ui?.min === 'number' ? (field.ui?.min as number) : 0}
            max={typeof field.ui?.max === 'number' ? (field.ui?.max as number) : 99}
            disableButtonAtLimit={!(typeof field.ui?.limitMessage === 'string' && (field.ui?.limitMessage as string).length > 0)}
            onLimitExceeded={typeof field.ui?.limitMessage === 'string' && (field.ui?.limitMessage as string).length > 0
              ? () => {
                  try {
                    window.dispatchEvent(new CustomEvent('iehub-alert', {
                      detail: {
                        severity: 'warning',
                        message: String(field.ui?.limitMessage),
                        variant: 'filled',
                        autoHide: true,
                        duration: 2000,
                      }
                    }));
                  } catch {}
                }
              : undefined}
          />
        );

      case 'date-with-clear':
        return (
          <DateWithClear
            key={field.fieldName}
            label={field.label}
            value={(fieldValue as Date) || null}
            onChange={(e: { target: { value: Date | null } }) => {
              form.setValue(field.fieldName, e.target.value, { shouldValidate: form.formState.isSubmitted });
              triggerFieldEvents('onChange', field, e.target.value);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            placeholder={field.ui?.placeholder || 'MM/DD/YYYY'}
          />
        );

      case 'count-label':
        return <ReactiveCountLabel key={field.fieldName} field={field} />;

      case 'question-group': {
        const questionGroupProps: any = {
          label: field.label ?? '',
          helpText: field.helpText ?? '',
          showCalculatedIncome: field.showCalculatedIncome ?? false,
          calculatedIncomeTooltip: field.calculatedIncomeTooltip,
          fieldName: field.fieldName,
          categoryKey: warningsCategoryKey,
          form: form,
          children: field.children?.map((childField) => {
            // Create a prefixed version of the child field for nested form structure
            const prefixedChildField = {
              ...childField,
              fieldName: `${field.fieldName}.${childField.fieldName}`
            };
            return renderField(prefixedChildField);
          })
        };
        
        if (field.tooltipContent) {
          questionGroupProps.mainTooltip = {
            content: field.tooltipContent,
            position: 'top'
          };
        }
        
        return (
          <QuestionGroup key={field.fieldName} {...questionGroupProps} />
        );
      }

      case 'text-with-tooltip': {
        return (
          <TextWithTooltip
            key={field.fieldName}
            name={field.fieldName}
            fieldPath={field.fieldName}
            value={fieldValue as string}
            onChange={(value) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            placeholder={field.ui?.placeholder ?? ''}
            tooltipVisible={field.tooltipVisible ?? false}
            tooltipContent={field.tooltipContent}
          />
        );
      }

      case 'select-toggle': {
        return (
          <SelectToggle
            key={field.fieldName}
            name={field.fieldName}
            label={field.label || ''}
            options={(field.options || []).map(opt => ({
              value: String(opt.value),
              label: opt.label,
              ...(opt.title && { title: opt.title }),
              ...(opt.description && { description: opt.description })
            }))}
            value={fieldValue as string}
            onChange={(value) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
          />
        );
      }

      case 'text-area': {
        return (
          <CollapsibleTextArea
            key={field.fieldName}
            label={field.label || ''}
            value={fieldValue as string}
            onChange={(value) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            placeholder={field.ui?.placeholder ?? ''}
            maxLength={field.maxLength}
          />
        );
      }

      case 'radiogroup': {
        const helperNode = (shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) 
          || (renderPrivacyHelper(field.helpText as string | undefined));
        return (
          <RadioGroup
            key={field.fieldName}
            label={field.label}
            value={fieldValue || ''}
            onChange={(value: string | number) => {
              form.setValue(field.fieldName, value, { shouldValidate: true });
              triggerFieldEvents('onChange', field, value);
            }}
            options={(field.options || []).map((opt) => ({
              // Accept multiple JSON shapes
              value: (opt.value as string | number) ?? (opt as any).id ?? (opt as any).key ?? (opt as any).code ?? (opt as any).label ?? (opt as any).title ?? '',
              title: (opt.title as string | undefined) ?? (opt.label as string | undefined) ?? String(
                (opt.value as string | number) ?? (opt as any).id ?? (opt as any).key ?? (opt as any).code ?? ''
              ),
              description: (opt.description as string | undefined) ?? (opt as any).helperText ?? (opt as any).subtitle ?? (opt as any).subTitle,
              disabled: Boolean((opt as any).disabled ?? (opt as any).isDisabled)
            }))}
            error={shouldShowError}
            helperText={helperNode as React.ReactNode}
            validation={validationRules}
            disabled={isFieldDisabled}
            variant="outlined"
          />
        );
      }

      case 'date':
      case 'time':
      case 'datetime':
        return (
          <DateField
            key={field.fieldName}
            type={field.dataType as 'date' | 'time' | 'datetime'}
            label={field.label}
            value={(fieldValue as Date) || null}
            onChange={(e: { target: { value: Date | null } }) => {
              // Only validate on change after the first submit attempt
              form.setValue(field.fieldName, e.target.value, { shouldValidate: form.formState.isSubmitted });
              triggerFieldEvents('onChange', field, e.target.value);
            }}
            error={shouldShowError}
            helperText={(shouldShowError ? (normalizedErrorMessage as string | undefined) : undefined) || (field.helpText as string | undefined)}
            validation={validationRules}
            disabled={isFieldDisabled}
            fullWidth
            deferValidation={true}
            helperTop={field.ui?.helperTop}
            helperAttached={field.ui?.helperAttached}
            helperBottom={field.ui?.helperBottom}
            showErrorBelow={field.ui?.showErrorBelow ?? true}
          />
        );

      default:
        return (
          <Box key={field.fieldName} sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Unsupported field type: {field.dataType}
            </Typography>
          </Box>
        );
    }
  }, [form, warningsCategoryKey]);

  const renderSection = useCallback((section: SectionConfig) => {
    // Get current form values for visibility evaluation
    const currentFormValues = form.getValues();
    
    // Filter out hidden fields, evaluate visibility conditions, and sort by order
    const visibleFields = section.fields
      .filter(field => {
        return computeIsVisible(field, currentFormValues);
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (visibleFields.length === 0) return null;

    // Generate dynamic fields based on counter values
    const generateDynamicFields = () => {
      const dynamicFields: FieldConfig[] = [];
      
      visibleFields.forEach(field => {
        if (field.dataType === 'date-with-clear') {
          // Check if this is a dependent DOB field that should be repeated
          if (field.fieldName === 'under16DependentsDOB') {
            const under16Count = Number(currentFormValues.under16Dependents || 0);
            for (let i = 1; i <= under16Count; i++) {
              const dynamicField: FieldConfig = {
                ...field,
                fieldName: `under16DependentsDOB_${i}`,
                label: `Dependent Child ${i} Date Of Birth`,
                order: (field.order || 0) + (i * 0.1), // Slight offset to maintain order
                ui: {
                  ...field.ui,
                  placeholder: `Enter dependent child ${i} date of birth`,
                  helperTop: `Please Enter Your dependent child ${i} date of birth`,
                }
              };
              dynamicFields.push(dynamicField);
            }
          } else if (field.fieldName === 'between16to18DependentsDOB') {
            const between16to18Count = Number(currentFormValues.between16to18Dependents || 0);
            for (let i = 1; i <= between16to18Count; i++) {
              const dynamicField: FieldConfig = {
                ...field,
                fieldName: `between16to18DependentsDOB_${i}`,
                label: `Dependent Child ${i} Date Of Birth`,
                order: (field.order || 0) + (i * 0.1), // Slight offset to maintain order
                ui: {
                  ...field.ui,
                  placeholder: `Enter dependent child ${i} date of birth`,
                  helperTop: `Please Enter Your dependent child ${i} date of birth`,
                }
              };
              dynamicFields.push(dynamicField);
            }
          } else {
            // Regular date field, add as is
            dynamicFields.push(field);
          }
        } else {
          // Non-date fields, add as is
          dynamicFields.push(field);
        }
      });
      
      return dynamicFields.sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    const fieldsToRender = generateDynamicFields();

    // Check if any field in this section has showErrorOnSection enabled
    const hasShowErrorOnSection = fieldsToRender.some(field => field.ui?.showErrorOnSection);
    
    // Collect section-level errors if showErrorOnSection is enabled
    const sectionErrors: string[] = [];
    if (hasShowErrorOnSection) {
      fieldsToRender.forEach(field => {
        if (field.ui?.showErrorOnSection) {
          const error = form.formState.errors[field.fieldName];
          if (error && error.message) {
            sectionErrors.push(error.message as string);
          }
        }
      });
    }

    return (
      <Paper 
        key={section.sectionName} 
        elevation={0}
        sx={{ 
          mb: 3,
          // p: 3,
          borderRadius: 0,
          backgroundColor: "none",
          // border: `1px solid ${theme.palette.divider}`,
          // borderRadius: 2,
          // transition: 'all 0.2s ease-in-out',
          // '&:hover': {
          //   borderColor: alpha(theme.palette.primary.main, 0.3),
          //   boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.04)}`,
          // }
        }}
      >
        {section.sectionLabel && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 0,
              gap: 1.5, // 12px gap as specified in CSS
              mb: 3, // Add margin bottom for spacing
              // Add red left border for entire section when there are errors
              ...(hasShowErrorOnSection && sectionErrors.length > 0 && {
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
                paddingLeft: 2,
                paddingBottom: 0,
                minHeight: 'auto',
                height: 'fit-content',
                paddingTop: 0,
                marginBottom: 3,
              })
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: (theme.palette as any).brand?.main || '#155F54', // Use brand color with fallback
                letterSpacing: '-0.02em',
                mb: 0 // Remove margin since we're using gap
              }}
            >
              {section.sectionLabel}
            </Typography>
            {section.sectionHelperText && (
              <SectionHelperText
                text={section.sectionHelperText}
                {...(section.sectionHelperTextHtml !== undefined && { isHtml: section.sectionHelperTextHtml })}
                {...(section.sectionHelperTextWordLimit !== undefined && { wordLimit: section.sectionHelperTextWordLimit })}
              />
            )}
            
            {/* Section-level error display */}
            {hasShowErrorOnSection && sectionErrors.length > 0 && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1,
                }}
              >
                {sectionErrors.map((errorMessage, index) => {
                  const msg = (() => {
                    const lower = String(errorMessage).trim().toLowerCase();
                    if (lower === 'invalid date' || lower === 'invaliddate' || (lower.includes('invalid') && lower.includes('date'))) {
                      return 'Please enter a valid date';
                    }
                    return errorMessage;
                  })();
                  return (
                  <Typography
                    key={index}
                    variant="body2"
                    color="error"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mb: 0,
                    }}
                  >
                    {msg}
                  </Typography>
                );})}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ p: 0 }}>
          <Grid container spacing={1.5}>
            {fieldsToRender.map(field => (
              <Grid
                key={field.fieldName}
                item
                xs={12}
                sm={field.size || 12}
                md={field.size || 12}
                lg={field.size || 12}
              >
                {renderField(field, field.ui?.showErrorOnSection || false)}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    );
  }, [renderField, form, parseVisibilityCondition, theme]);

  if (!schema) {
    return (
      <Container maxWidth="lg" disableGutters sx={{ py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No schema provided
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters>
      {/* Form Header */}
      { hideHeader === false &&
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Typography 
          variant="h3" 
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: theme.palette.text.primary,
            mb: 1
          }}
        >
          {schema.formName}
        </Typography>
        {schema.collectionName && (
          <Typography 
            variant="body1" 
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500
            }}
          >
            {schema.collectionName}
          </Typography>
        )}
      </Paper>}

      <FormProvider {...form}>
        <form id={formId} onSubmit={form.handleSubmit(handleSubmit)}>
          {/* Form Sections */}
          {schema.sections.map(section => renderSection(section))}

          {/* Form Actions */}
          {hideActions === false && <Paper
            elevation={0}
            sx={{
              mt: 4,
              p: 3,
              backgroundColor: theme.palette.background.default,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => form.reset()}
              disabled={loading}
              size="large"
              sx={{
                minWidth: 120,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Reset Form
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={loading}
              disabled={loading}
              size="large"
              sx={{
                minWidth: 160,
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              {loading ? 'Submitting...' : 'Submit Form'}
            </Button>
          </Paper>}
        </form>
      </FormProvider>

      {/* Privacy Policy Drawer */}
      <InfoDrawer open={privacyOpen} onClose={() => setPrivacyOpen(false)} content={privacyContent} />

      {/* Debug info - only in development */}
      {/* {import.meta.env.DEV && (
        <Paper
          elevation={0}
          sx={{ 
            mt: 4, 
            p: 3, 
            backgroundColor: alpha(theme.palette.info.main, 0.04),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}
        >
          <Typography 
            variant="subtitle2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.info.dark,
              mb: 2
            }}
          >
            Debug Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Form Name</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.formName}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Version</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.version}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Total Fields</Typography>
              <Typography variant="body2" fontWeight={500}>
                {schema.sections.reduce((total, section) => total + section.fields.length, 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Sections</Typography>
              <Typography variant="body2" fontWeight={500}>{schema.sections.length}</Typography>
            </Grid>
            {schema._id && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">MongoDB ID</Typography>
                <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                  {schema._id.$oid}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )} */}
    </Container>
  );
}

export default Screen;